import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import { Fab } from "@mui/material";
import { useGetChatConversationsQuery } from "../../../../app/communicationApiSlice";
import UserAvatar from "../../../common/UserAvatar";

const CONV_CACHE_KEY = 'comm_conversations_cache_v1';
function readConvCache() {
  try { return JSON.parse(localStorage.getItem(CONV_CACHE_KEY) || 'null'); } catch { return null; }
}
function writeConvCache(data) {
  try { localStorage.setItem(CONV_CACHE_KEY, JSON.stringify(data)); } catch { /* quota */ }
}

function timeAgo(value) {
  if (!value) return "";
  const diff = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function participantName(conv) {
  const names = (conv.participants || [])
    .filter((p) => !p.roles?.includes("student"))
    .map((p) => p.name)
    .filter(Boolean);
  return names.join(", ") || "Conversation";
}

function participantPhoto(conv) {
  const p = (conv.participants || []).find((p) => !p.roles?.includes("student"));
  return p?.photo || null;
}

export default function ConversationList({ activeId, onSelect, onNew }) {
  const { data, isFetching } = useGetChatConversationsQuery(undefined, {
    pollingInterval: 30000,
  });
  // Offline-first: seed from localStorage, replace with live data when it arrives
  const [cached] = useState(() => readConvCache());
  const rawConversations = data?.conversations || cached?.conversations || [];
  if (data?.conversations) writeConvCache(data);
  const conversations = rawConversations;
  const [search, setSearch] = useState("");

  const filtered = search.trim()
    ? conversations.filter((c) =>
        participantName(c).toLowerCase().includes(search.toLowerCase()) ||
        c.student?.name?.toLowerCase().includes(search.toLowerCase())
      )
    : conversations;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRight: 1,
        borderColor: "divider",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography variant="subtitle1" fontWeight={800}>
            Messages
          </Typography>
          <Fab
            size="small"
            color="primary"
            onClick={onNew}
            title="New conversation"
            sx={{ boxShadow: 1 }}
          >
            <AddIcon fontSize="small" />
          </Fab>
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="disabled" />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
        />
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isFetching && conversations.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {filtered.length === 0 && !isFetching && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary" variant="body2">
              {search ? "No matching conversations" : "No messages yet. Start a conversation!"}
            </Typography>
          </Box>
        )}
        <List disablePadding>
          {filtered.map((conv) => (
            <Box key={conv.id}>
              <ListItemButton
                selected={conv.id === activeId}
                onClick={() => onSelect(conv.id)}
                sx={{ px: 2, py: 1.5 }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                  <UserAvatar
                    src={participantPhoto(conv)}
                    name={participantName(conv)}
                    sx={{ width: 42, height: 42, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant="body2"
                        fontWeight={conv.unread_count > 0 ? 800 : 600}
                        noWrap
                        sx={{ maxWidth: "75%" }}
                      >
                        {participantName(conv)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" flexShrink={0}>
                        {timeAgo(conv.last_message?.created_at)}
                      </Typography>
                    </Stack>
                    {conv.student?.name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        re: {conv.student.name}
                      </Typography>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant="caption"
                        color={conv.unread_count > 0 ? "text.primary" : "text.secondary"}
                        fontWeight={conv.unread_count > 0 ? 600 : 400}
                        noWrap
                        sx={{ maxWidth: "80%" }}
                      >
                        {conv.last_message?.body || "No messages yet"}
                      </Typography>
                      {conv.unread_count > 0 && (
                        <Chip
                          size="small"
                          color="primary"
                          label={conv.unread_count}
                          sx={{ height: 18, fontSize: "0.65rem", flexShrink: 0 }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </ListItemButton>
              <Divider component="li" />
            </Box>
          ))}
        </List>
      </Box>
    </Box>
  );
}
